<template>
  <section class="widget-form-renderer" id="form-stet">
    <WButton
      v-if="isBackButtonVisible"
      class="widget-form-renderer_back-btn group"
      :class="componentStyles?.button?.back"
      type="ghost"
      :disabled="disabled"
      @click="openConfirmModal"
    >
      <WidgetIcon :icon="themeIcons.chevronLeft" class="widget-primary-icon h-6 w-6" />
      <p class="widget-form-renderer_title group-hover:underline">
        {{ config.settings?.type !== StepType.CLAIMS ? title : 'Back' }}
      </p>
    </WButton>
    <JsonForm
      :data="data"
      :schema="schema"
      :uischema="uischema"
      @change="onChange"
      @error="onError"
      :validation-mode="validationMode"
    ></JsonForm>
    <template v-if="options?.submit">
      <WButton
        type="primary"
        class="widget-form-renderer_submit"
        :loading="isLoading"
        :disabled="isLoading || isSubmitDisabled"
        @click="validateCaptcha"
      >
        <WidgetIcon
          v-if="!checkTheme('suzuki', 'common') && themeIcons.arrowRight"
          :icon="themeIcons.arrowRight"
          height="1.75rem"
          class="!transition-none !delay-0"
        />
        {{ options.actionLabel || 'Submit' }}
        <WidgetIcon
          v-if="checkTheme('suzuki') && themeIcons.arrowRight"
          :icon="themeIcons.arrowRight"
          height="1.75rem"
          class="widget-form-renderer_submit-icon !transition-none !delay-0"
        />
      </WButton>
    </template>
    <div v-else-if="options?.reportAnother" class="widget-form-renderer_another">
      <p
        v-if="typeof options?.reportAnother === 'object' && !!options?.reportAnother?.title"
        class="widget-form-renderer_another--title"
      >
        {{ options?.reportAnother?.title }}
      </p>
      <WButton type="primary" class="widget-form-renderer_another--link" @click="backWithReset">
        {{
          (typeof options?.reportAnother === 'string' ? options?.reportAnother : options?.reportAnother?.label) ||
          'Report another incident'
        }}
      </WButton>
    </div>
    <div
      v-if="errorMessage"
      class="mt-4 w-full rounded-custom border-2 border-negative-500 bg-negative-100 p-4 font-bold text-negative-500"
    >
      {{ errorMessage }}
    </div>
    <div
      v-if="(errors?.errors?.length || errorMessages?.length) && validationMode === 'ValidateAndShow'"
      class="mt-4 w-full rounded-custom border-2 border-negative-500 bg-negative-100 p-4 font-bold text-negative-500"
    >
      <h4 class="title3-bold">Required fields are missing or not valid:</h4>
      <ul>
        <template v-if="errors?.errors?.length">
          <li class="mt-4" v-for="(error, index) in errors.errors" :key="index">
            <div class="grid grid-flow-col items-start justify-start gap-x-4">
              <div class="flex h-full w-full items-center justify-start">
                <div class="h-2 w-2 min-w-2 rounded-full bg-negative-500"></div>
              </div>
              <p class="body1 upper-first-letter">
                {{
                  error.parentSchema?.errorMessage ||
                  error.parentSchema?.properties?.[error.params.missingProperty]?.errorMessage ||
                  error.parentSchema?.properties?.[error.params.missingProperty]?.title
                }}
              </p>
            </div>
          </li>
        </template>
        <template v-if="errorMessages?.length">
          <li class="mt-4" v-for="(error, index) in errorMessages" :key="index">
            <div class="grid grid-flow-col items-start justify-start gap-x-4">
              <div class="flex h-full w-full items-center justify-start">
                <div class="h-2 w-2 min-w-2 rounded-full bg-negative-500"></div>
              </div>
              <p class="body1 upper-first-letter">
                {{ error }}
              </p>
            </div>
          </li>
        </template>
      </ul>
    </div>
    <Modal v-if="isConfirmModalOpen" v-model="isConfirmModalOpen" hide-close>
      <template #header>
        <h2 class="title2-bold">Go back?</h2>
      </template>
      <div class="widget-confirm-modal_body">
        <p class="body1">All information you've entered will be deleted</p>
      </div>
      <template #footer>
        <div class="widget-confirm-modal_footer">
          <WButton type="secondary" class="widget-confirm-modal_btn_close" @click="isConfirmModalOpen = false">
            No, keep editing
          </WButton>
          <WButton type="primary" class="widget-confirm-modal_btn" @click="back"> Yes, go back </WButton>
        </div>
      </template>
    </Modal>
  </section>
</template>

<script setup lang="ts">
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import { JsonSchema, Layout, UISchemaElement, ValidationMode } from '@jsonforms/core';
  import { JsonFormsChangeEvent } from '@jsonforms/vue';
  import {
    defineAsyncComponent,
    ref,
    nextTick,
    onMounted,
    onBeforeUnmount,
    computed,
    onUnmounted,
    watch,
    WatchStopHandle,
  } from 'vue';
  import { request } from 'src/utils/request';
  import { checkTheme, config, getComponentStyle, resetConfig, themeIcons } from 'src/utils/config';
  import { useWidgetError, useSubmission } from '../../form/util/composition';
  import { FormOptions, StepType } from 'src/types/widget';
  import JsonForm from 'forms/JsonForm.vue';
  import WButton from 'src/components/base/Button.vue';
  import { API_PATHS, isSource, Source } from 'src/types/api';
  import { useDebounceFn } from '@vueuse/core';
  import { loadRecaptchaScript, isRecaptchaAvailable, validateV3, resetReCaptcha } from 'src/utils/recaptcha';
  import { sendAnalytics } from 'src/utils/analytics';
  import merge from 'lodash/merge';
  import { _load, initialConfig } from 'src/index';

  const Modal = defineAsyncComponent(() => import('src/components/base/ConfirmModal.vue'));
  const { emitChange, emitError, emitSuccess } = useSubmission();

  const emit = defineEmits<{
    (event: 'next'): void;
    (event: 'back'): void;
  }>();

  export interface Props {
    schema: JsonSchema;
    uischema?: Layout | UISchemaElement;
    options?: FormOptions;
    title: string;
    disabled?: boolean;
    isBackButtonVisible?: boolean;
    stepKey?: string;
    freezeUpdate?: boolean;
    state?: Source;
  }
  const props = defineProps<Props>();
  const componentStyles = getComponentStyle();
  const isSubmitDisabled = ref(false);
  const isRecaptchaUsed = computed(() => !(config.value.settings?.type === StepType.CLAIMS || !isRecaptchaAvailable));

  onMounted(async () => {
    nextTick(() => {
      scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    });
  });
  onMounted(async () => {
    if (!isRecaptchaUsed.value) return;
    await loadRecaptchaScript();
  });
  onBeforeUnmount(() => {
    if (!isRecaptchaUsed.value) return;
    resetReCaptcha();
  });
  const data = ref<Source | object>(props.state || ({} as Source));
  if (
    !props.state &&
    config.value.sessionData &&
    config.value.sessionData.currentSession &&
    'stepKey' in config.value.sessionData.currentSession &&
    props.stepKey === config.value.sessionData?.currentSession?.stepKey &&
    config.value.sessionData?.currentSession &&
    config.value.sessionTracking
  ) {
    data.value = config.value.sessionData?.currentSession;
  }
  if (config.value.state) {
    data.value = merge(config.value.state, data.value) as Source;
  }
  const { validate, errorMessages } = useWidgetError();
  const validationMode = ref<ValidationMode>('ValidateAndHide');
  const errors = ref<Pick<JsonFormsChangeEvent, 'errors'>>();
  const isConfirmModalOpen = ref(false);
  const updateData = useDebounceFn(sendUpdate, 500, { maxWait: 5000 });

  function confirmExit(e: BeforeUnloadEvent) {
    e.preventDefault();
    return true;
  }
  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', confirmExit);
  });

  function addExitPrevent() {
    window.addEventListener('beforeunload', confirmExit);
  }
  let unregister: WatchStopHandle;
  // eslint-disable-next-line prefer-const
  unregister = watch(data, () => {
    addExitPrevent();
    unregister?.();
  });

  function sendUpdate(clear = false) {
    if (!config.value.sessionTracking || !config.value.currentUserId || (!clear && !Object.keys(data.value).length))
      return;
    if (config.value.sessionData?.id) {
      request.put(
        API_PATHS.session,
        config.value.sessionData?.id,
        JSON.parse(
          JSON.stringify(
            {
              ...data.value,
              ...(!clear && { stepKey: props.stepKey }),
            },
            (key, value) => (value === undefined ? '' : value)
          )
        )
      );
    }
  }

  const triggerChange = useDebounceFn(emitChange, 500);
  async function onChange(value: typeof data.value) {
    if (props.freezeUpdate) return;
    data.value = value;
    if ('stepKey' in value) {
      triggerChange(value);
    }
    if (config.value.sessionTracking) {
      updateData();
    }
  }

  function onError(error: Pick<JsonFormsChangeEvent, 'errors'>) {
    errors.value = error;
  }

  const errorMessage = ref('');
  const isLoading = ref(false);
  async function validateCaptcha() {
    if (errors.value?.errors?.length || errorMessages.value?.length) {
      await validate();
      validationMode.value = 'ValidateAndShow';
      return;
    }

    errorMessage.value = '';
    // if insured is true, we will submit the result without checking recaptcha
    if (!isRecaptchaUsed.value) {
      return submitResult();
    }
    isLoading.value = true;
    try {
      const token = await validateV3();
      await submitResult(token);
    } catch (error) {
      console.log('error :>> ', error);
    } finally {
      isLoading.value = false;
    }
  }
  function isObjectKey<K extends PropertyKey, T>(obj: T, key: K): obj is T & { [P in K]: { [Q in P]: object } }[K] {
    return (
      obj &&
      typeof obj === 'object' &&
      key in obj &&
      obj[key as unknown as keyof T] &&
      typeof obj[key as unknown as keyof T] === 'object'
    );
  }

  async function submitResult(recaptchaToken?: string) {
    if (!isSource(data.value)) {
      return;
    }
    isLoading.value = true;
    const { missingProperties, ...payload } = data.value;

    const submissionPayload = {
      ...payload,
      user: {
        ...config.value?.driver,
        ...(isObjectKey(missingProperties, 'selectedUser') ? missingProperties.selectedUser : {}),
        id: config.value.currentUserId,
      },
      ...(config.value.settings?.type === StepType.CLAIMS
        ? {
            insurance: {
              provider: config.value.insurance?.provider,
              number: config.value.insurance?.number,
              validFrom: config.value.insurance?.validFrom,
              validTo: config.value.insurance?.validTo,
              ...(isObjectKey(missingProperties, 'selectedInsurance') ? missingProperties.selectedInsurance : {}),
            },
          }
        : {}),
      vehicle: {
        ...config.value?.vehicle,
      },
      stepKey: props.stepKey,
      ...((config.value as unknown as Record<string, unknown>).testMode === true ? { testMode: true } : {}),
      stepType: config.value.steps.type,
      ...(recaptchaToken ? { recaptchaToken } : {}),
    };
    sendAnalytics('event', 'submission', submissionPayload);
    const [result, error] = await request.post(API_PATHS.submission, submissionPayload);
    if (error) {
      errorMessage.value = error.message;
      await emitError(error);
      return;
    }
    await emitSuccess(result);

    if (config.value.sessionTracking && config.value.sessionData) {
      config.value.sessionData.currentSession = {};
      config.value.sessionData.submissionId = result?.id;
      config.value.sessionData.submissionReference = result.reference;
    }
    config.value.sessionData.submission = result;
    config.value.reference = result.reference;
    data.value = {};
    emit('next');
    isLoading.value = false;
  }

  function openConfirmModal() {
    const confirmOn = [
      'single-vehicle-form',
      'single-vehicle-light-form',
      'patch-single-vehicle-form',
      'optional-single-vehicle-form',
      'optional-single-vehicle-personal-form',
      'repair-finder-form',
      'repair-finder-form-us',
      'multiple-vehicle-form',
      'repair-form',
      'retailer-repair-form',
      'repair-referral-form',
      'accident-referral-form',
      'bosch-referral-form',
      'jlr-referral-form',
      'jlr-referral-form-v2',
      'suzuki-referral-form',
      'jlr-referral-form-v3',
      'retailer-referral-form',
    ];
    if (!config.value.sessionTracking || !confirmOn.includes(String(config.value.selectedClaim))) {
      config.value.reference = '';
      nextTick(() => {
        emit('back');
      });
      return;
    }
    isConfirmModalOpen.value = true;
  }

  function back() {
    resetConfig();
    isConfirmModalOpen.value = false;
    if (config.value.sessionData?.currentSession) {
      config.value.sessionData.currentSession = {};
    }
    config.value.reference = '';
    nextTick(() => {
      emit('back');
    });
  }
  function backWithReset() {
    config.value.context = {};
    back();
    triggerChange({} as Source);
    _load(initialConfig.apiKeyOrClientId, initialConfig.optionsOrToken, initialConfig.callbacks, true);
  }

  onUnmounted(() => {
    data.value = {};
    if (config.value.sessionTracking && props.stepKey?.includes('-form')) {
      if (!config.value.sessionData?.submissionId) {
        sendUpdate(true);
      }
    }
  });
</script>
